import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Grid,
  Typography,
  useMediaQuery,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import CouponCard from '../../../components/coupon/CouponCard'
import CouponDetail from '../../../components/coupon/CouponDetail'
import { WEB_NAV_HEIGHT } from '../../../helpers/static-constants'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { ICoupon } from '../../../types/leaderboardTypes'
import PurchaseCoupon from './components/PurchaseCoupon'
import BackIconButton from '../../../components/ui/button/BackIconButton'
import { fetchRewardsCatalog, fetchWalletBalance, RewardItem, WalletBalanceResponse } from '../../../services/rewardsService'
import { errorLog } from '../../../utils/log-helper'
import { ROUTES } from '../../../helpers/routes-helper'
import { logEvent } from '../../../analytics/analytics'

export const DRAWER_WIDTH = 392
export const CONTENT_WIDTH = 1280

interface ICouponsProp {
  name: 'my-coupon' | 'redeem-points'
}

// Helper function to convert RewardItem to ICoupon for compatibility
const rewardItemToCoupon = (reward: RewardItem): ICoupon => {
  return {
    id: reward.code,
    image: reward.imageUrl ?? 'https://via.placeholder.com/400x232?text=Coupon',
    title: reward.name,
    description: reward.description,
    code: reward.code,
    pointsRequired: reward.pointsCost,
    termsAndConditions: [], // Will be populated from API later if needed
  }
}

// Helper functions for wallet balance
const getWalletAvailableDollars = (b?: WalletBalanceResponse | null): number =>
  ((b?.walletAvailableCents ?? 0) / 100)

const getMonthlyCapDollars = (b?: WalletBalanceResponse | null): number =>
  ((b?.monthlyLimitCents ?? 0) / 100)

const getRedeemedThisMonthDollars = (b?: WalletBalanceResponse | null): number =>
  ((b?.alreadyRedeemedCents ?? 0) / 100)

const formatUsd = (n: number): string => n.toFixed(2)

const Coupons: React.FC<ICouponsProp> = ({ name }) => {
  const { navigate } = useAppUtilities()
  const isBetween960And1300 = useMediaQuery(
    '(min-width:960px) and (max-width:1300px)',
  )
  const [openCouponDetailDrawer, setOpenCouponDetailDrawer] = useState(false)
  const [width, setWidth] = useState(window.innerWidth)
  const [openPurchaseCoupon, setOpenPurchaseCoupon] = useState(false)
  const [selectedReward, setSelectedReward] = useState<RewardItem | null>(null)
  const [openInstructions, setOpenInstructions] = useState(false)
  const [selectedRewardForInstructions, setSelectedRewardForInstructions] = useState<RewardItem | null>(null)
  
  // State for catalog data (only used for 'redeem-points')
  const [rewards, setRewards] = useState<RewardItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [walletBalance, setWalletBalance] = useState<WalletBalanceResponse | null>(null)
  // eslint-disable-next-line
  const [loadingWallet, setLoadingWallet] = useState(false)

  useEffect(() => {
    const handleResize = (): void => setWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Fetch catalog data and wallet balance for 'redeem-points' type
  useEffect(() => {
    if (name !== 'redeem-points') {
      return
    }

    const loadData = async (): Promise<void> => {
      try {
        setLoading(true)
        setError(null)
        const [catalog, balance] = await Promise.all([
          fetchRewardsCatalog(),
          fetchWalletBalance().catch((e) => {
            errorLog('Failed to load wallet balance', e)
            return { walletAvailableCents: 0, monthlyLimitCents: 0, alreadyRedeemedCents: 0, pointsBalance: 0 }
          }),
        ])
        setRewards(catalog.rewards ?? [])
        setWalletBalance(balance)
      } catch (e) {
        errorLog('Failed to load rewards catalog', e)
        setError('Unable to load rewards right now. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    void loadData()
  }, [name])

  // points_balance_viewed: fire once per mount when the rewards balance has loaded — the top of the
  // view->redeem funnel (previously the whole earn/view/redeem loop was dark).
  const pointsBalanceViewedRef = React.useRef(false)
  useEffect(() => {
    if (
      name === 'redeem-points' &&
      walletBalance &&
      !pointsBalanceViewedRef.current
    ) {
      pointsBalanceViewedRef.current = true
      logEvent('points_balance_viewed', {
        screen_name: 'rewards',
      })
    }
  }, [name, walletBalance])

  // Use catalog data for 'redeem-points', static data for 'my-coupon'
  const displayData = name === 'redeem-points' 
    ? rewards.map(rewardItemToCoupon)
    : couponData

  return (
    <Box>
      <Helmet>
        <title>Resactly | My Coupons</title>
      </Helmet>
      <Container
        component="main"
        sx={{
          flex: 1,
        }}
      >
        <Box
          sx={{
            width: {
              md: openCouponDetailDrawer
                ? isBetween960And1300
                  ? `calc(100% - ${DRAWER_WIDTH}px)`
                  : `calc(100% - ${
                      DRAWER_WIDTH - (width - CONTENT_WIDTH - 10) / 2
                    }px)`
                : '100%',
            },
            willChange: 'width',
            transition: 'width 300ms cubic-bezier(0.25, 0.1, 0.25, 1)',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            my={{ xs: 3, md: 5 }}
            gap={1.5}
          >
            <BackIconButton
              sx={{ alignSelf: 'flex-start' }}
              onClick={() => {
                if (name === 'my-coupon') {
                  navigate('/')
                } else {
                  // Use router history back to return to previous page
                  navigate(-1)
                }
              }}
            />
            <Typography variant="h3">
              {name === 'my-coupon'
                ? 'My Coupon'
                : 'Rewards'}
            </Typography>
          </Box>
          {name === 'redeem-points' && (
            <>
              {/* Wallet Header Card */}
              <Card
                sx={{
                  mb: 4,
                  borderRadius: '16px',
                  backgroundColor: 'secondary.50',
                  p: 3,
                }}
              >
                <Typography variant="h6" component="h2" color="text.primary" gutterBottom>
                  Your Rewards Wallet
                </Typography>
                {loadingWallet ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    {walletBalance && (
                      <>
                        <Typography variant="h5" component="div" color="text.primary" fontWeight={600} gutterBottom>
                          Available this month: ${formatUsd(getWalletAvailableDollars(walletBalance))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Monthly cap: ${formatUsd(getMonthlyCapDollars(walletBalance))} • Redeemed: ${formatUsd(getRedeemedThisMonthDollars(walletBalance))}
                        </Typography>
                      </>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Points unlock rewards. Credits are what you spend.
                    </Typography>
                    {walletBalance && getWalletAvailableDollars(walletBalance) === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        You have $0.00 available right now. Earn points and stay active to unlock your monthly reward.
                      </Typography>
                    ) : null}
                  </>
                )}
              </Card>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Points unlock rewards. Credits spend rewards. Points are not cash.
              </Typography>
            </>
          )}
          <Box>
            {loading && (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Loading rewards...
                </Typography>
              </Box>
            )}
            {error && (
              <Box py={4}>
                <Typography color="error" variant="body1">
                  {error}
                </Typography>
              </Box>
            )}
            {!loading && !error && (
              <Grid container spacing={2.5}>
                {name === 'redeem-points' 
                  ? rewards.map((reward) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={isBetween960And1300 && openCouponDetailDrawer ? 6 : 4}
                        xl={openCouponDetailDrawer ? 4 : 3}
                        key={reward.code}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          willChange: 'width',
                          transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <CouponCard
                          reward={reward}
                          onCardClick={() => {
                            // reward_viewed: opening a reward's detail is the "view" step of the
                            // loyalty loop (a real interaction, not a passive catalog render).
                            logEvent('reward_viewed', {
                              reward_type: reward.cashLike ? 'cash' : 'coupon',
                              reward_id: reward.code,
                              screen_name: 'rewards',
                            })
                            setSelectedReward(reward)
                            setOpenCouponDetailDrawer(true)
                          }}
                          onShowInstructions={(reward) => {
                            setSelectedRewardForInstructions(reward)
                            setOpenInstructions(true)
                          }}
                          type={name}
                        />
                      </Grid>
                    ))
                  : displayData.map((coupon) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={isBetween960And1300 && openCouponDetailDrawer ? 6 : 4}
                        xl={openCouponDetailDrawer ? 4 : 3}
                        key={coupon.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          willChange: 'width',
                          transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <CouponCard
                          coupon={coupon}
                          key={coupon.id}
                          onCardClick={() => setOpenCouponDetailDrawer(true)}
                          type={name}
                        />
                      </Grid>
                    ))}
              </Grid>
            )}
          </Box>
        </Box>
      </Container>

      <Drawer
        variant="persistent"
        open={openCouponDetailDrawer}
        anchor="right"
        sx={{
          flexShrink: 0,
          width: { xs: '100vw', md: openCouponDetailDrawer ? DRAWER_WIDTH : 0 },
          willChange: 'width',
          transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', md: DRAWER_WIDTH },
            marginTop: {
              xs: `0px`,
              md: `${WEB_NAV_HEIGHT}px`,
            },
            height: {
              xs: '100vh',
              md: `calc(100vh - ${WEB_NAV_HEIGHT}px)`,
            },
          },
        }}
      >
        {((name === 'redeem-points' && selectedReward !== null) ?? (name !== 'redeem-points' && displayData.length > 0)) && (
          <CouponDetail
            reward={name === 'redeem-points' ? selectedReward : null}
            coupon={name === 'redeem-points' ? (selectedReward ? rewardItemToCoupon(selectedReward) : null) : (displayData[0] ?? null)}
            onClose={() => {
              setOpenCouponDetailDrawer(false)
              setSelectedReward(null)
            }}
            onGetCoupon={() => setOpenPurchaseCoupon(true)}
            type={name}
            walletBalance={walletBalance}
            onShowInstructions={(reward) => {
              setSelectedRewardForInstructions(reward)
              setOpenInstructions(true)
            }}
          />
        )}
      </Drawer>
      {((name === 'redeem-points' && selectedReward !== null) ?? (name !== 'redeem-points' && displayData.length > 0)) && (
        <PurchaseCoupon
          reward={name === 'redeem-points' ? selectedReward : null}
          coupon={name === 'redeem-points' ? (selectedReward ? rewardItemToCoupon(selectedReward) : null) : (displayData[0] ?? null)}
          open={openPurchaseCoupon}
          handleClose={() => setOpenPurchaseCoupon(false)}
          walletBalance={walletBalance}
        />
      )}
      <Dialog 
        open={openInstructions} 
        onClose={() => setOpenInstructions(false)} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle>How to redeem</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
            {selectedRewardForInstructions?.usageInstructions ?? 'No instructions available for this reward.'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => {
              setOpenInstructions(false)
              navigate(ROUTES.REDEEM_POINTS)
            }}
          >
            See all redemption options
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInstructions(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Coupons

export const couponData: ICoupon[] = [
  {
    id: '1',
    image:
      'https://img.freepik.com/free-psd/modern-banner-breakfast-restaurant_23-2148351348.jpg',

    title: '20% Discount Voucher Above $50',
    description:
      'Lorem ipsum dolor sit amet consectetur. Arcu quis ornare tempor duis.',
    code: '564789123',
    expiryDate: '2024-10-10',
    pointsRequired: 340,
    termsAndConditions: [
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate cras amet massa. Diam donec in ut tortor nisi.',
      'Blandit ornare. Molestie habitant facilisi dui nunc mattis tellus odio nec non.',
      'Lorem ipsum dolor sit amet consectetur.',
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate crastis tellus odio nec non.',
    ],
  },
  {
    id: '2',
    image:
      'https://img.freepik.com/free-psd/restaurant-voucher-template-design_23-2148410402.jpg?semt=ais_hybrid&w=740',

    title: '20% Discount Voucher Above $50',
    description:
      'Lorem ipsum dolor sit amet consectetur. Arcu quis ornare tempor duis.',
    code: '564789173',
    expiryDate: '2025-05-06',
    pointsRequired: 340,
    termsAndConditions: [
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate cras amet massa. Diam donec in ut tortor nisi.',
      'Blandit ornare. Molestie habitant facilisi dui nunc mattis tellus odio nec non.',
      'Lorem ipsum dolor sit amet consectetur.',
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate crastis tellus odio nec non.',
    ],
  },
  {
    id: '3',
    image:
      'https://img.freepik.com/free-psd/american-food-voucher-template_23-2148477327.jpg?semt=ais_hybrid&w=740',

    title: '20% Discount Voucher Above $50',
    description:
      'Lorem ipsum dolor sit amet consectetur. Arcu quis ornare tempor duis.',
    code: '564789124',
    expiryDate: '2025-05-08',
    pointsRequired: 380,
    termsAndConditions: [
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate cras amet massa. Diam donec in ut tortor nisi.',
      'Blandit ornare. Molestie habitant facilisi dui nunc mattis tellus odio nec non.',
      'Lorem ipsum dolor sit amet consectetur.',
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate crastis tellus odio nec non.',
    ],
  },
  {
    id: '4',
    image:
      'https://img.freepik.com/free-psd/colorful-food-template-design_23-2151900750.jpg',

    title: '20% Discount Voucher Above $50',
    description:
      'Lorem ipsum dolor sit amet consectetur. Arcu quis ornare tempor duis.',
    code: '564789873',
    expiryDate: '2025-05-10',
    pointsRequired: 390,
    termsAndConditions: [
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate cras amet massa. Diam donec in ut tortor nisi.',
      'Blandit ornare. Molestie habitant facilisi dui nunc mattis tellus odio nec non.',
      'Lorem ipsum dolor sit amet consectetur.',
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate crastis tellus odio nec non.',
    ],
  },
  {
    id: '5',
    image:
      'https://img.freepik.com/free-psd/delicious-food-landing-page-template_23-2150056441.jpg?semt=ais_hybrid&w=740',

    title: '20% Discount Voucher Above $50',
    description:
      'Lorem ipsum dolor sit amet consectetur. Arcu quis ornare tempor duis.',
    code: '564780873',
    expiryDate: '2024-05-10',
    pointsRequired: 400,
    termsAndConditions: [
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate cras amet massa. Diam donec in ut tortor nisi.',
      'Blandit ornare. Molestie habitant facilisi dui nunc mattis tellus odio nec non.',
      'Lorem ipsum dolor sit amet consectetur.',
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate crastis tellus odio nec non.',
    ],
  },
  {
    id: '6',
    image:
      'https://img.freepik.com/free-psd/delicious-food-landing-page-template_23-2150056441.jpg?semt=ais_hybrid&w=740',

    title: '20% Discount Voucher Above $50',
    description:
      'Lorem ipsum dolor sit amet consectetur. Arcu quis ornare tempor duis.',
    code: '564780873',
    expiryDate: '2024-05-10',
    pointsRequired: 400,
    termsAndConditions: [
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate cras amet massa. Diam donec in ut tortor nisi.',
      'Blandit ornare. Molestie habitant facilisi dui nunc mattis tellus odio nec non.',
      'Lorem ipsum dolor sit amet consectetur.',
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate crastis tellus odio nec non.',
    ],
  },
  {
    id: '7',
    image:
      'https://img.freepik.com/free-psd/delicious-food-landing-page-template_23-2150056441.jpg?semt=ais_hybrid&w=740',

    title: '20% Discount Voucher Above $50',
    description:
      'Lorem ipsum dolor sit amet consectetur. Arcu quis ornare tempor duis.',
    code: '564780873',
    expiryDate: '2024-05-10',
    pointsRequired: 400,
    termsAndConditions: [
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate cras amet massa. Diam donec in ut tortor nisi.',
      'Blandit ornare. Molestie habitant facilisi dui nunc mattis tellus odio nec non.',
      'Lorem ipsum dolor sit amet consectetur.',
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate crastis tellus odio nec non.',
    ],
  },
  {
    id: '8',
    image:
      'https://img.freepik.com/free-psd/delicious-food-landing-page-template_23-2150056441.jpg?semt=ais_hybrid&w=740',

    title: '20% Discount Voucher Above $50',
    description:
      'Lorem ipsum dolor sit amet consectetur. Arcu quis ornare tempor duis.',
    code: '564780873',
    expiryDate: '2024-05-10',
    pointsRequired: 400,
    termsAndConditions: [
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate cras amet massa. Diam donec in ut tortor nisi.',
      'Blandit ornare. Molestie habitant facilisi dui nunc mattis tellus odio nec non.',
      'Lorem ipsum dolor sit amet consectetur.',
      'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate crastis tellus odio nec non.',
    ],
  },
]
